import type React from 'react'
import { useLayoutEffect, useRef, useState } from 'react'

import { useValueSetter } from './useValueSetter'

export function useFilledState<
  T extends HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
> (
  defaultValue: string | number | readonly string[] | undefined,
  value: string | number | readonly string[] | undefined,
  onChange?: (event: React.ChangeEvent<T>) => void
) {
  const [filled, setFilled] = useState(!!defaultValue || !!value)
  const ref = useRef<T>(null)

  /**
   * Tracks changes made to the input value directly via the DOM (for example,
   * by `react-hook-form`).
   */
  useValueSetter(ref, (value) => {
    setFilled(!!value)
  })

  /**
   * To get an accurate value for select inputs we need to check the
   * `value` of the element after it has rendered. We cannot rely solely on the
   * `defaultValue` or `value` props because a `<select>` element's value will
   * default to the value of the first `<option>` element if an initial value
   * was either not provided or does not match any of the `<option>`s.
   *
   * This same handling also keeps the filled state in sync with the value prop
   * when it changes outside of an interaction with the input.
   */
  useLayoutEffect(() => {
    if (ref.current) setFilled(ref.current.value !== '')
  }, [value])

  function handleChange (event: React.ChangeEvent<T>) {
    setFilled(event.target.value !== '')

    onChange?.(event)
  }

  return {
    filled,
    handleChange,
    ref,
  }
}
